import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { ITicket } from 'models/ticket_model';
import {
  createTicketService,
  getTicketProfesorService,
  getTicketMonitorService,
} from 'repositories/ticket_repository';
import type { ITicketForm } from 'views/Ticket/components/form-ticket';

interface TicketState {
  tickets: ITicket[];
  loading: boolean;
  error: string | null;
}

const initialState: TicketState = {
  tickets: [],
  loading: false,
  error: null,
};

export const createTicket = createAsyncThunk(
  'ticket/createTicket',
  async (data: ITicketForm, { rejectWithValue }) => {
    try {
      return await createTicketService(data);
    } catch {
      return rejectWithValue('Error al crear ticket');
    }
  },
);

export const cargarTicketsProfesor = createAsyncThunk<ITicket[], void, { rejectValue: string }>(
  'ticket/cargarTicketsProfesor',
  async (_arg, { rejectWithValue }) => {
    try {
      const { rows } = await getTicketProfesorService();
      return rows ?? [];
    } catch {
      return rejectWithValue('Error al cargar tickets');
    }
  },
);

export const cargarTicketsMonitor = createAsyncThunk<ITicket[], { status?: string } | undefined, { rejectValue: string }>(
  'ticket/cargarTicketsMonitor',
  async (filtros = {}, { rejectWithValue }) => {
    try {
      const { rows } = await getTicketMonitorService(filtros);
      return rows ?? [];
    } catch {
      return rejectWithValue('Error al cargar tickets');
    }
  },
);

export const ticketSlice = createSlice({
  name: 'ticket',
  initialState,
  reducers: {
    setTickets: (state, action: PayloadAction<ITicket[]>) => {
      state.tickets = action.payload;
    },
  },
  extraReducers: (builder) => {
    const setTicketsReducer = (state: TicketState, action: { payload: ITicket[] }) => {
      state.loading = false;
      state.tickets = action.payload;
    };
    builder
      .addCase(cargarTicketsProfesor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cargarTicketsProfesor.fulfilled, setTicketsReducer)
      .addCase(cargarTicketsProfesor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(cargarTicketsMonitor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cargarTicketsMonitor.fulfilled, setTicketsReducer)
      .addCase(cargarTicketsMonitor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setTickets } = ticketSlice.actions;
export default ticketSlice.reducer;
